import React from 'react';
import type { Note } from 'sr-types/lib/search/v1/graphql';
import type { EntityName, EntitySecurity } from '../../auth/Security';
import { SECURITY_ACTIONS } from '../../auth/Security';
import type { SecuredActionType } from '../../components/ActionType';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import type { Reference } from '../../reference/reference';
import type { DataRowSecurityActionPermissions } from '../types';
import type { DataListActionHandlers, DataListCustomRowAction } from './types';

export const getDataListActions = ({
    resolvedRowPermissions,
    entityName,
    entitySecurity,
    item,
    onEdit,
    onShare,
    onDelete,
    onAttachments,
    onNotes,
    onActivateOrInactivateHandle,
    onCustomAttribute,
    onTagHandle,
    customRowActions = []
}: {
    resolvedRowPermissions: DataRowSecurityActionPermissions;
    /**
     * Optional only to support callers that still rely on the legacy permission fallback.
     * Make required once those callers forward it and the missing-entity fallback is removed.
     */
    entityName?: EntityName;
    entitySecurity?: EntitySecurity;
    item: Record<string, unknown>;
    customRowActions?: DataListCustomRowAction[];
} & DataListActionHandlers): SecuredActionType[] => {
    const canEdit = resolvedRowPermissions[SECURITY_ACTIONS.EDIT];
    const canShare = resolvedRowPermissions[SECURITY_ACTIONS.SHARE];
    const canDelete = resolvedRowPermissions[SECURITY_ACTIONS.DELETE];
    const canComment = resolvedRowPermissions[SECURITY_ACTIONS.COMMENT];
    const canInactivate = resolvedRowPermissions[SECURITY_ACTIONS.INACTIVATE];
    const canActivate = resolvedRowPermissions[SECURITY_ACTIONS.ACTIVATE];

    const actions: SecuredActionType[] = [];

    if (canShare && onShare) {
        actions.push({
            securityAction: SECURITY_ACTIONS.SHARE,
            entityName,
            entitySecurity,
            icon: Icons.Share,
            onClick: (event) => {
                event.stopPropagation();
                onShare(item);
            }
        });
    }

    if (canEdit && onEdit) {
        actions.push({
            securityAction: SECURITY_ACTIONS.EDIT,
            entityName,
            entitySecurity,
            icon: Icons.EditOutlined,
            onClick: (event) => {
                event.stopPropagation();
                onEdit(item.id as string, item);
            }
        });
    }

    if (canDelete && onDelete) {
        actions.push({
            securityAction: SECURITY_ACTIONS.DELETE,
            entityName,
            entitySecurity,
            icon: Icons.Delete,
            onClick: (event) => {
                event.stopPropagation();
                onDelete(item.id as string);
            }
        });
    }

    if (canEdit && onCustomAttribute) {
        actions.push({
            securityAction: SECURITY_ACTIONS.EDIT,
            entityName,
            entitySecurity,
            icon: Icons.ResourceFields,
            onClick: (event) => {
                event.stopPropagation();
                onCustomAttribute(item);
            }
        });
    }

    if (canEdit && onTagHandle) {
        actions.push({
            securityAction: SECURITY_ACTIONS.EDIT,
            entityName,
            entitySecurity,
            icon: Icons.LabelOutlined,
            onClick: (event) => {
                event.stopPropagation();
                onTagHandle(item);
            }
        });
    }

    if (canComment && onAttachments) {
        actions.push({
            securityAction: SECURITY_ACTIONS.COMMENT,
            entityName,
            entitySecurity,
            icon: Icons.Attachments,
            onClick: (event) => {
                event.stopPropagation();
                onAttachments(item.reference as Reference, item.name as string);
            }
        });
    }

    if (canComment && onNotes) {
        actions.push({
            securityAction: SECURITY_ACTIONS.COMMENT,
            entityName,
            entitySecurity,
            icon: Icons.Notes,
            onClick: (event) => {
                event.stopPropagation();
                onNotes(item.reference as Reference, item.name as string, item.currentStatusNote as Note | undefined);
            }
        });
    }

    if ((canInactivate || canActivate) && onActivateOrInactivateHandle) {
        const title =
            (canInactivate && <I18n token={'action.inactive'} />) || (canActivate && <I18n token={'action.active'} />);
        actions.push({
            securityAction: canInactivate ? SECURITY_ACTIONS.INACTIVATE : SECURITY_ACTIONS.ACTIVATE,
            entityName,
            entitySecurity,
            label: title,
            onClick: (event) => {
                event.stopPropagation();
                onActivateOrInactivateHandle(item, canInactivate, canActivate);
            }
        });
    }

    customRowActions
        .filter((action) => resolvedRowPermissions[action.securityAction])
        .forEach((action) => {
            actions.push({
                securityAction: action.securityAction,
                entityName,
                entitySecurity,
                icon: action.icon,
                onClick: (event) => {
                    event.stopPropagation();
                    action.onClick(item);
                }
            });
        });

    return actions;
};
